import { CreateInspectionResponse } from '@services';

/**
 * What has been filled in but has not reached the server yet, kept on the device.
 *
 * IndexedDB rather than localStorage for two reasons. `clearStoredSession` calls
 * `localStorage.clear()`, so a session running out - which is exactly what happens when a connection
 * is gone for hours - would take the draft with it. And photos do not fit: localStorage holds strings
 * in a ~5MB budget shared with everything else, while a checklist can carry forty of them.
 *
 * Everything here fails soft. Private windows, blocked site data and full quotas all end with storage
 * simply not being there, and an inspection has to keep working without it - the draft is a safety
 * net, not the record.
 */

const DB_NAME = 'umoto-offline';
const DB_VERSION = 1;
const ANSWERS_STORE = 'inspectionAnswers';
const PHOTOS_STORE = 'inspectionPhotos';
const PHOTOS_BY_INSPECTION = 'inspectionId';

export interface InspectionAnswerDraft {
  inspectionId: string;
  answers: CreateInspectionResponse[];
  savedAt: number;
  /**
   * Set when the server refused this outright - the inspection was deleted, or it belongs to another
   * garage. Background sync leaves these alone rather than asking again every minute forever. The
   * record is kept: refusing to send it is not a reason to destroy what somebody typed.
   */
  blockedAt?: number;
}

export interface InspectionPhotoDraft {
  /** The same key the thumbnail carries in the UI, so the two can be matched up after a reload. */
  key: string;
  inspectionId: string;
  subCategoryItemId: number;
  name: string;
  contentType: string;
  /** Base64 of the already-compressed image - what the upload would have sent. */
  content: string;
  savedAt: number;
  /** See InspectionAnswerDraft.blockedAt. */
  blockedAt?: number;
}

let dbPromise: Promise<IDBDatabase | null> | undefined;

const openDb = (): Promise<IDBDatabase | null> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(ANSWERS_STORE))
          db.createObjectStore(ANSWERS_STORE, { keyPath: 'inspectionId' });

        if (!db.objectStoreNames.contains(PHOTOS_STORE))
          db
            .createObjectStore(PHOTOS_STORE, { keyPath: 'key' })
            .createIndex(PHOTOS_BY_INSPECTION, 'inspectionId');
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

  return dbPromise;
};

/** Resolves undefined on any failure - callers treat that as "no draft", never as an error. */
const run = async <T>(
  storeName: string,
  mode: IDBTransactionMode,
  request: (store: IDBObjectStore) => IDBRequest
): Promise<T | undefined> => {
  const db = await openDb();
  if (!db) return undefined;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(storeName, mode);
      const result = request(transaction.objectStore(storeName));

      result.onsuccess = () => resolve(result.result as T);
      result.onerror = () => resolve(undefined);
      transaction.onabort = () => resolve(undefined);
      transaction.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
};

export const saveAnswerDraft = async (inspectionId: string, answers: CreateInspectionResponse[]) => {
  if (answers.length === 0) {
    await clearAnswerDraft(inspectionId);
    return;
  }

  const draft: InspectionAnswerDraft = { inspectionId, answers, savedAt: Date.now() };

  await run(ANSWERS_STORE, 'readwrite', (store) => store.put(draft));
};

export const loadAnswerDraft = async (
  inspectionId: string
): Promise<CreateInspectionResponse[]> => {
  const draft = await run<InspectionAnswerDraft | undefined>(ANSWERS_STORE, 'readonly', (store) =>
    store.get(inspectionId)
  );

  return draft?.answers ?? [];
};

export const clearAnswerDraft = (inspectionId: string) =>
  run(ANSWERS_STORE, 'readwrite', (store) => store.delete(inspectionId));

export const savePhotoDraft = (draft: Omit<InspectionPhotoDraft, 'savedAt'>) =>
  run(PHOTOS_STORE, 'readwrite', (store) => store.put({ ...draft, savedAt: Date.now() }));

export const deletePhotoDraft = (key: string) =>
  run(PHOTOS_STORE, 'readwrite', (store) => store.delete(key));

/**
 * Whether drafts can be kept at all. False in a browser where site data is blocked or the quota is
 * gone; the screen then says so, because without it the only copy of the work is the open tab.
 *
 * Private browsing is not this case - IndexedDB works there in current browsers, it simply goes when
 * the private window does, the same as every other kind of storage.
 */
export const isDraftStorageAvailable = async (): Promise<boolean> => (await openDb()) !== null;

/**
 * How long a draft that never reached the server is kept.
 *
 * Only a backstop against storage filling up - a draft that sends is cleared the moment it does, so
 * anything here was never sent at all. The two costs are nowhere near equal: too long wastes a little
 * disk, too short throws away a checklist somebody filled in. A technician working a Friday on site
 * and coming back after the weekend is already three days.
 */
const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

let sweptThisSession = false;

/**
 * Drops drafts for inspections nothing has touched in a fortnight, answers and photos together.
 *
 * Judged per inspection on its most recent activity rather than per record: a photo taken on day one
 * of an inspection still being filled in on day twelve is not stale, and half-deleting an inspection
 * that is still in use would be worse than keeping both.
 */
export const sweepStaleDrafts = async (): Promise<number> => {
  if (sweptThisSession) return 0;
  sweptThisSession = true;

  const answers =
    (await run<InspectionAnswerDraft[]>(ANSWERS_STORE, 'readonly', (store) => store.getAll())) ?? [];
  const photos =
    (await run<InspectionPhotoDraft[]>(PHOTOS_STORE, 'readonly', (store) => store.getAll())) ?? [];

  const lastTouched = new Map<string, number>();
  const touch = (inspectionId: string, at: number) =>
    lastTouched.set(inspectionId, Math.max(lastTouched.get(inspectionId) ?? 0, at));

  answers.forEach((draft) => touch(draft.inspectionId, draft.savedAt));
  photos.forEach((draft) => touch(draft.inspectionId, draft.savedAt));

  const cutoff = Date.now() - DRAFT_MAX_AGE_MS;
  const stale = new Set(
    Array.from(lastTouched)
      .filter(([, at]) => at < cutoff)
      .map(([inspectionId]) => inspectionId)
  );

  if (stale.size === 0) return 0;

  await Promise.all([
    ...answers
      .filter((draft) => stale.has(draft.inspectionId))
      .map((draft) => clearAnswerDraft(draft.inspectionId)),
    ...photos.filter((draft) => stale.has(draft.inspectionId)).map((draft) => deletePhotoDraft(draft.key)),
  ]);

  return stale.size;
};

/** Everything waiting, across every inspection. For the background sync, which is not on a screen. */
export const loadAllAnswerDrafts = async (): Promise<InspectionAnswerDraft[]> =>
  (await run<InspectionAnswerDraft[]>(ANSWERS_STORE, 'readonly', (store) => store.getAll())) ?? [];

export const loadAllPhotoDrafts = async (): Promise<InspectionPhotoDraft[]> =>
  (await run<InspectionPhotoDraft[]>(PHOTOS_STORE, 'readonly', (store) => store.getAll())) ?? [];

export const blockAnswerDraft = async (inspectionId: string) => {
  const draft = await run<InspectionAnswerDraft | undefined>(ANSWERS_STORE, 'readonly', (store) =>
    store.get(inspectionId)
  );

  if (draft) await run(ANSWERS_STORE, 'readwrite', (store) => store.put({ ...draft, blockedAt: Date.now() }));
};

export const blockPhotoDraft = async (key: string) => {
  const draft = await run<InspectionPhotoDraft | undefined>(PHOTOS_STORE, 'readonly', (store) =>
    store.get(key)
  );

  if (draft) await run(PHOTOS_STORE, 'readwrite', (store) => store.put({ ...draft, blockedAt: Date.now() }));
};

export const loadPhotoDrafts = async (inspectionId: string): Promise<InspectionPhotoDraft[]> => {
  const drafts = await run<InspectionPhotoDraft[] | undefined>(PHOTOS_STORE, 'readonly', (store) =>
    store.index(PHOTOS_BY_INSPECTION).getAll(inspectionId)
  );

  return (drafts ?? []).sort((a, b) => a.savedAt - b.savedAt);
};
